import React, { useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Popconfirm,
  Select,
  Switch,
  Table,
} from "antd";
import Flex from "components/shared-components/Flex";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../data/hooks/firebase/useAuth";
import { deleteDoc, doc, updateDoc } from "firebase/firestore";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Modal from "antd/lib/modal/Modal";
import { FIRESTORE_DISCOUNT_CODES_TABLE, FIRESTORE_REGIONS_TABLE } from "constants/FirestoreConstant";
import { firestore } from "../../../configs/FirebaseConfig";
import locale from "antd/es/date-picker/locale/nl_NL";
import { DATE_FORMAT_DD_MM_YYYY } from "../../../constants/DateConstant";
import i18n from "../../../translations/i18n";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  DISCOUNT_CODE_TYPE_FIRST_ORDER,
  DISCOUNT_CODE_TYPE_MULTIPLE_TIMES,
  DISCOUNT_CODE_TYPE_ONCE,
} from "../../../constants/DiscountConstants";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { CURRENCY } from "../../../constants/DeliveryConstants";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useDiscountCodes } from "../../../data/hooks/firestore/useDiscountCodes";
import Loading from "../../../components/shared-components/Loading";

dayjs.extend(isBetween);
const { Option } = Select;

const DiscountList = () => {
  const { data: discountCodes, loading, error } = useDiscountCodes();
  const { email } = useAuth();
  const [editForm] = Form.useForm();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [codeToEdit, setCodeToEdit] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const { t } = useTranslation();
  const [discountType, setDiscountType] = useState(DISCOUNT_CODE_TYPE_MULTIPLE_TIMES);

  const dropdownMenu = (row) => (
    <Menu>
      <Menu.Item
        onClick={() => {
          setCodeToEdit(row);
          editForm.setFieldsValue({
            code: row.code,
            discount: !!row.discountPercentage ? row.discountPercentage : 0,
            amount: !!row.discountAmount ? row.discountAmount : 0,
            start_date: dayjs(row.startDate, DATE_FORMAT_DD_MM_YYYY),
            expiration_date: dayjs(row.expirationDate, DATE_FORMAT_DD_MM_YYYY),
            type: !!row.type ? row.type : DISCOUNT_CODE_TYPE_MULTIPLE_TIMES,
            maxUses: !!row.maxUses ? row.maxUses : null,
          });
          setActive(row.active);
          setDiscountType(row.type);
          setStartDate(dayjs(row.startDate, DATE_FORMAT_DD_MM_YYYY));
          setEditModalVisible(true);
        }}
      >
        <Flex alignItems="center">
          <span className="ml-2">{t("edit")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item>
        <Popconfirm
          placement="bottom"
          title={t("delete_discount_code")}
          onConfirm={() => deleteDiscountCode(row)}
          okText={t("yes")}
          cancelText={t("no")}
        >
          <a onClick={(e) => e.preventDefault}>{t("delete")}</a>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: t("discount_code"),
      dataIndex: "code",
    },
    {
      title: t("discount"),
      render: (data) => (
        <span>{!!data.discountPercentage ? data.discountPercentage + "%" : CURRENCY + data.discountAmount}</span>
      ),
    },
    {
      title: t("how_many_times"),
      dataIndex: "type",
      render: (_, data) => (
        <span>
          {t(
            !!data.type && data.type === DISCOUNT_CODE_TYPE_MULTIPLE_TIMES
              ? !!data.maxUses
                ? data.maxUses + "x"
                : "unlimited"
              : data.type === DISCOUNT_CODE_TYPE_FIRST_ORDER
                ? "first_order"
                : "once",
          )}
        </span>
      ),
    },
    {
      title: t("start_date"),
      dataIndex: "startDate",
      render: (date) => <span>{date}</span>,
    },
    {
      title: t("expiration_date"),
      dataIndex: "expirationDate",
      render: (date) => <span>{date}</span>,
    },
    {
      title: t("status"),
      render: (_) => <Flex alignItems="center">{getActiveStatus(_)}</Flex>,
    },
    {
      title: t("times_used"),
      dataIndex: "usedBy",
      render: (data) => <span>{!!data ? data.length : 0}</span>,
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  const getActiveStatus = (discount) => {
    const today = dayjs();
    const isBetween = today.isBetween(
      dayjs(discount.startDate, DATE_FORMAT_DD_MM_YYYY),
      dayjs(discount.expirationDate, DATE_FORMAT_DD_MM_YYYY),
      undefined,
      "[]",
    );
    if (!discount.active)
      return (
        <>
          <Badge status="warning" />
          <span style={{ marginLeft: "6px" }}>{t("inactive")}</span>
        </>
      );
    if (!isBetween)
      return (
        <>
          <Badge status="warning" />
          <span style={{ marginLeft: "6px" }}>{t("inactive_period")}</span>
        </>
      );
    else
      return (
        <>
          <Badge status="success" />
          <span style={{ marginLeft: "6px" }}>{t("active")}</span>
        </>
      );
  };

  /**
   * Submit edit form
   */
  const onSubmitEdit = () => {
    let originalDiscountCode = codeToEdit;
    editForm
      .validateFields()
      .then(async () => {
        setSubmitLoading(true);
        let fields = editForm.getFieldsValue();
        if (!!fields.discount && fields.discount > 0 && !!fields.amount && fields.amount > 0) {
          message.error(t("notifications.discount_percentage_and_amount"));
          setSubmitLoading(false);
        } else if (fields.discount === 0 && fields.amount === 0) {
          message.error(t("notifications.discount_percentage_and_amount_null"));
          setSubmitLoading(false);
        } else {
          let expirationDate = dayjs(fields.expiration_date).format(DATE_FORMAT_DD_MM_YYYY);
          let startDate = dayjs(fields.start_date).format(DATE_FORMAT_DD_MM_YYYY);
          let active = typeof fields.active == "undefined" ? originalDiscountCode.active : fields.active;
          try {
            await updateDoc(
              doc(
                firestore,
                FIRESTORE_REGIONS_TABLE,
                COMPANY_COUNTRY_CODE,
                FIRESTORE_DISCOUNT_CODES_TABLE,
                originalDiscountCode.id,
              ),
              {
                discountPercentage: !!fields.discount ? fields.discount : null,
                discountAmount: !!fields.amount ? fields.amount : null,
                startDate: startDate,
                expirationDate: expirationDate,
                active: active,
                updatedAt: dayjs().unix(),
                updatedBy: email,
                type: fields.type,
                maxUses: !!fields.maxUses ? fields.maxUses : null,
              },
            );
            setEditModalVisible(false);
            setCodeToEdit(null);
            setSubmitLoading(false);
            message.success(t("notifications.entry_updated", 4));
          } catch (err) {
            setSubmitLoading(false);
            message.error(err.message);
          }
        }
      })
      .catch((err) => {
        setSubmitLoading(false);
        console.log(err);
      });
  };

  /**
   * Remove discount code from firestore
   * @param discountCodeObj Promotion to delete
   */
  const deleteDiscountCode = async (discountCodeObj) => {
    try {
      await deleteDoc(
        doc(
          firestore,
          FIRESTORE_REGIONS_TABLE,
          COMPANY_COUNTRY_CODE,
          FIRESTORE_DISCOUNT_CODES_TABLE,
          discountCodeObj.id,
        ),
      );
      message.success(t("notifications.discount_code_deleted", 4));
    } catch (err) {
      message.error(err.message);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Col sm={24} md={18}>
      <Card>
        <div className="table-responsive">
          <Table
            columns={tableColumns}
            dataSource={discountCodes}
            rowKey="id"
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("table_no_discount_code")} />,
            }}
            scroll={{ x: 800 }}
          />
        </div>
      </Card>
      <Modal
        title={t("edit_promotion")}
        open={editModalVisible}
        footer={null}
        onCancel={() => setEditModalVisible(false)}
      >
        <Form layout="vertical" form={editForm} name="advanced_search" className="ant-advanced-search-form">
          <Form.Item
            name="code"
            label={t("discount_code")}
            rules={[
              {
                required: true,
                message: t("form.enter_discount_code"),
              },
            ]}
          >
            <Input placeholder={t("discount_code")} disabled />
          </Form.Item>
          <Form.Item
            name="discount"
            label={t("discount_percentage")}
            tooltip={{ title: i18n.t("form.discount_percentage_tooltip"), icon: <InfoCircleOutlined /> }}
            rules={[
              {
                required: true,
                message: t("form.enter_discount_percentage"),
              },
            ]}
          >
            <InputNumber
              className="w-100"
              min={0}
              max={100}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace("%", "")}
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label={t("discount_amount")}
            tooltip={{ title: i18n.t("form.discount_amount_tooltip"), icon: <InfoCircleOutlined /> }}
            rules={[
              {
                required: true,
                message: t("form.enter_discount_amount"),
              },
            ]}
          >
            <InputNumber className="w-100" formatter={(value) => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} />
          </Form.Item>
          <Form.Item
            name="type"
            label={t("discount_code_type")}
            tooltip={{ title: t("form.discount_type_tooltip"), icon: <InfoCircleOutlined /> }}
            rules={[
              {
                required: true,
                message: t("form.pick_value"),
              },
            ]}
          >
            <Select onChange={(value) => setDiscountType(value)}>
              <Option value={DISCOUNT_CODE_TYPE_MULTIPLE_TIMES}>{t("multiple")}</Option>
              <Option value={DISCOUNT_CODE_TYPE_ONCE}>{t("once")}</Option>
              <Option value={DISCOUNT_CODE_TYPE_FIRST_ORDER}>{t("new_users")}</Option>
            </Select>
          </Form.Item>
          {discountType === DISCOUNT_CODE_TYPE_MULTIPLE_TIMES && (
            <Form.Item
              name="maxUses"
              label={t("max_amount_of_uses")}
              tooltip={{ title: t("form.discount_times_tooltip"), icon: <InfoCircleOutlined /> }}
              rules={[
                {
                  required: true,
                  message: t("form.pick_value"),
                },
              ]}
            >
              <InputNumber className="w-100" placeholder={t("max_amount_of_uses")} />
            </Form.Item>
          )}
          <Form.Item
            name="start_date"
            label={t("start_date")}
            rules={[
              {
                required: true,
                message: t("form.choose_start_date"),
              },
            ]}
          >
            <DatePicker
              locale={locale}
              format={DATE_FORMAT_DD_MM_YYYY}
              placeholder={t("start_date_placeholder")}
              disabledDate={(current) => {
                return current < dayjs().startOf("day");
              }}
              style={{ width: "100%" }}
              onChange={(value) => {
                setStartDate(value);
                let expDate = editForm.getFieldValue("expiration_date");
                if (!!expDate && expDate < value) editForm.resetFields(["expiration_date"]);
              }}
            />
          </Form.Item>
          <Form.Item
            name="expiration_date"
            label={t("expiration_date")}
            rules={[
              {
                required: true,
                message: t("form.choose_expiration_date"),
              },
            ]}
          >
            <DatePicker
              locale={locale}
              format={DATE_FORMAT_DD_MM_YYYY}
              placeholder={t("expiration_date_placeholder")}
              disabledDate={(current) => {
                return current < dayjs().endOf("day") || (!!startDate && current < startDate.endOf("day"));
              }}
              showToday={false}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item name="active" label={t("status")}>
            <Switch onClick={() => setActive(!active)} checked={active} />
          </Form.Item>
          <Button type="primary" onClick={() => onSubmitEdit()} htmlType="submit" loading={submitLoading}>
            {t("save")}
          </Button>
        </Form>
      </Modal>
    </Col>
  );
};

export default DiscountList;
